/**
 * Typed bindings for /api/aggregator — other channels' posts republished into
 * the company's own channels through the company's bot — and the few things
 * the pages work out about them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountSummary } from "./accounts";
import { api } from "./api";
import type { Tone } from "./status";

export type AggStatus = "active" | "paused";
export type DeliveryStatus =
  | "pending"
  | "queued"
  | "review"
  | "publishing"
  | "published"
  | "skipped"
  | "rejected"
  | "expired"
  | "failed"
  | "removed";

export interface TextRun {
  text: string;
  styles: string[];
  href: string | null;
}

/** A text two ways: as the editor shows it, and piece by piece for drawing. */
export interface RichText {
  markup: string;
  runs: TextRun[];
}

export interface PostMedia {
  kind: "photo" | "video" | "gif";
  url: string;
  thumb: string | null;
  /** A video's frame and length, when known — to lay an album out as Telegram does. */
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  /** Only a small copy is kept (the post is in the channel): a video plays in Telegram. */
  small?: boolean;
}

export interface AggBot {
  id: string;
  username: string;
  name: string;
  problem: string | null;
  checked_at: string | null;
  channels: number;
  /** The account that made the bot; null when it is no longer in the panel. */
  account_seq_no: number | null;
}

/** A channel one of the company's accounts runs, as "add a channel" offers it. */
export interface OwnChannel {
  id: string;
  title: string;
  username: string | null;
  account_seq_no: number;
  account_label: string;
  is_creator: boolean;
  /** Already filled by the aggregator. */
  connected: boolean;
}

export interface AiProvider {
  id: string;
  name: string;
  base_url: string;
  model: string;
  requests_per_minute: number;
  position: number;
  is_active: boolean;
  key_hint: string;
  last_error: string | null;
  last_error_at: string | null;
  last_ok_at: string | null;
}

export type FilterKind =
  | "stop_words"
  | "must_words"
  | "forward"
  | "forward_from"
  | "reply"
  | "link"
  | "channel_link"
  | "language"
  | "min_length"
  | "max_length"
  | "no_media"
  | "has_media"
  | "missing_media"
  | "marked_ad"
  | "looks_like_ad";

export interface FilterRule {
  id: string;
  kind: FilterKind;
  value: string;
  action: "skip" | "review";
  source_id: string | null;
  enabled: boolean;
}

export interface Replacement {
  id: string;
  find: string;
  replace: string;
  source_id: string | null;
}

export interface ChannelSettings {
  cleanups: {
    links: "keep" | "remove" | "replace";
    links_replace_with: string;
    remove_mentions: boolean;
    remove_hashtags: boolean;
    remove_footer: boolean;
    remove_emoji: boolean;
    /** Only the post's first paragraph is taken — from every source (null)
     *  or from this one. */
    first_paragraph: boolean;
    first_paragraph_source_id: string | null;
    replacements: Replacement[];
  };
  signature: string;
  credit_source: boolean;
  filters: FilterRule[];
  duplicates: {
    enabled: boolean;
    threshold: number;
    action: "skip" | "review";
    window_hours: number;
  };
  ai: {
    mode: "off" | "rewrite" | "shorten" | "translate" | "custom";
    style: "neutral" | "news" | "casual" | "expert";
    language: string;
    prompt: string;
    ads: "off" | "skip" | "strip";
    topic: string;
    on_failure: "review" | "original" | "skip";
  };
  /** Translation without a language model — DeepL, on the company's key. */
  translate: {
    enabled: boolean;
    language: string;
    on_failure: "review" | "original" | "skip";
  };
  schedule: {
    days: number[];
    hour_from: number;
    hour_to: number;
    interval_minutes: number;
    max_per_day: number;
    delay_minutes: number;
    stale_hours: number;
    slots: string[];
  };
  publishing: { silent: boolean; link_preview: boolean };
  moderation: "auto" | "all";
  min_views: number;
  check_still_there: boolean;
}

export interface ChannelSummary {
  id: string;
  title: string;
  username: string | null;
  members_count: number | null;
  status: AggStatus;
  /** Why nothing can be published right now (a code); null when all is well. */
  problem: string | null;
  problem_detail: string | null;
  bot: { id: string; username: string; problem: string | null } | null;
  sources: number;
  sources_broken: number;
  queued: number;
  /** Read and not yet put through the channel's settings. */
  pending: number;
  review: number;
  published_24h: number;
  failed_24h: number;
  last_post_at: string | null;
  next_post_at: string | null;
  ai_mode: ChannelSettings["ai"]["mode"];
  moderation: ChannelSettings["moderation"];
  created_at: string;
  /** The company's clock the schedule runs on: moments ahead are shown by it. */
  timezone: string;
  /** The fill running in the channel — or the last one, for a day after it
   *  ended; null when there is neither. */
  fill: Fill | null;
  /** Who publishes; by account — the accounts that run the channel, by
   *  number: its creator first, then the other admins of the panel. */
  publisher: "account" | "bot";
  managers: number[];
}

export type FillMode = "last" | "days" | "best";

/** One source's way through a fill. */
export interface FillSource {
  id: string;
  name: string;
  via: "page" | "account";
  /** waiting — its account is not in the chat yet; reading — paged or
   *  scrolled back; fetching — its account fetches the files a portion at a
   *  time; gone — taken off the channel. */
  state: "waiting" | "reading" | "fetching" | "done" | "failed" | "gone";
  /** Posts chosen; of them, what the channel had already; posts taken. */
  found: number;
  already: number;
  taken: number;
  /** A fill that skips what was taken: posts the channel had, passed over. */
  skipped: number;
  /** Why it stopped, or what it waits for (a code). */
  detail: string | null;
}

/** A channel filled with its sources' older posts. */
export interface Fill {
  id: string;
  status: "running" | "done" | "stopped";
  mode: FillMode;
  count: number | null;
  days: number | null;
  gap_min: number;
  gap_max: number;
  /** The posts the channel took already are passed over for the next ones. */
  skip_taken: boolean;
  sources: FillSource[];
  /** Its posts: taken in all; out; on their way; waiting for a person; let
   *  go (a rule, a repeat, Telegram's refusal); let go by "stop". */
  taken: number;
  published: number;
  waiting: number;
  review: number;
  dropped: number;
  stopped: number;
  /** Posts of a closed source whose files its account is still to fetch
   *  (0 once the fill has ended). */
  to_fetch: number;
  /** What goes out of it, all told: "out: published of total". */
  total: number;
  /** Roughly when its last post goes out — once every source is read. */
  ends_at: string | null;
  created_at: string;
  finished_at: string | null;
}

/** What one fill may ask for (the server says the same). */
export const FILL_LIMITS = { posts: 300, days: 90, gapMinutes: 24 * 60 } as const;

export interface FillInput {
  source_ids: string[];
  mode: FillMode;
  /** last, best: posts of each source. */
  count: number | null;
  /** days, best: the period. */
  days: number | null;
  gap_min: number;
  gap_max: number;
  skip_taken: boolean;
}

export interface AggSource {
  id: string;
  username: string;
  title: string | null;
  subscribers: number | null;
  status: AggStatus;
  problem: string | null;
  problem_detail: string | null;
  last_checked_at: string | null;
  last_post_at: string | null;
  shared_with: number;
  taken_7d: number;
  published_7d: number;
  /** How it is read: its public page — or an account of the fleet that is a
   *  member of it (a closed channel, a group). */
  via: "page" | "account";
  /** The account that reads it; null — none has been given yet. */
  account_seq_no: number | null;
  /** That account's way in: wanted — not let in yet, requested — an admin
   *  has to accept it. null for a page. */
  join_state: "wanted" | "requested" | "joined" | null;
  is_group: boolean;
  /** Where a person can open it. */
  link: string;
}

export interface ChannelDetail extends ChannelSummary {
  settings: ChannelSettings;
  source_list: AggSource[];
  has_ai_provider: boolean;
  /** The company connected DeepL. */
  has_translator: boolean;
}

export interface PagePost {
  message_id: number;
  url: string;
  text: RichText;
  media: PostMedia[];
  media_missing: string[];
  posted_at: string | null;
  views: number | null;
  is_reply: boolean;
  forwarded_from: string | null;
}

export interface SourcePreview {
  username: string;
  title: string | null;
  subscribers: number | null;
  has_preview: boolean;
  posts: PagePost[];
}

export interface SourcesAdded {
  added: number;
  already: number;
  /** Of the added: those an account will read — it joins by itself. */
  via_account: number;
  unsupported: string[];
  skipped: number;
  troubled: { username: string; problem: string }[];
  /** The sources the channel got — offered for a fill next. */
  added_ids: string[];
}

export interface DryRunItem {
  source_username: string;
  source_title: string | null;
  url: string;
  posted_at: string | null;
  original: RichText;
  media: PostMedia[];
  media_missing: string[];
  status: "queued" | "review" | "skipped";
  reason: string | null;
  rule_id: string | null;
  detail: string | null;
  text: RichText | null;
  ai_provider: string | null;
}

export interface DryRun {
  items: DryRunItem[];
  unread: string[];
  ai_skipped: boolean;
}

export interface Delivery {
  id: string;
  channel_id: string;
  status: DeliveryStatus;
  reason: string | null;
  rule_id: string | null;
  detail: string | null;
  source_id: string | null;
  source_username: string;
  source_title: string | null;
  source_url: string;
  posted_at: string | null;
  original: RichText;
  text: RichText | null;
  media: PostMedia[];
  media_missing: string[];
  edited: boolean;
  ai_provider: string | null;
  duplicate_of: string | null;
  expected_at: string | null;
  /** When a queued post is let go if its turn has not come by then. */
  expires_at: string | null;
  published_at: string | null;
  url: string | null;
  /** Failed half-way with a part of itself left in the channel. */
  left_in_channel: boolean;
  views: number | null;
  source_views: number | null;
  attempts: number;
  created_at: string;
  /** The fill that took this older post; null for a fresh one. */
  fill_id: string | null;
  /** Written in the panel by a person. */
  own: boolean;
  pinned: boolean;
  /** The moment a person set for it to go out. */
  publish_at: string | null;
}

export interface DeliveriesPage {
  total: number;
  items: Delivery[];
}

export interface ChannelStats {
  days: number;
  taken: number;
  published: number;
  skipped: number;
  review: number;
  failed: number;
  ai_tokens: number;
  by_day: { day: string; published: number; skipped: number; failed: number }[];
  by_reason: { reason: string; rule_id: string | null; count: number }[];
  by_source: {
    source_id: string | null;
    username: string;
    title: string | null;
    taken: number;
    published: number;
    skipped: number;
    avg_views: number | null;
  }[];
}

export type DeliveryView = "queue" | "review" | "journal" | "published";

/**
 * The account that runs a channel publishes into it — no bot (the owner's
 * word, 20 October; the server's `engine.PUBLISHER`). The bot's screens are
 * kept, and shown only when this says "bot".
 */
export const PUBLISHER: "account" | "bot" = "account";

/** A text that differs when the channel's account publishes: its `_account`
 *  twin first, the bot's own after. For `t()`. */
export function byPublisher(key: string): string[] {
  return PUBLISHER === "account" ? [`${key}_account`, key] : [key];
}

/** The keys a channel's problem is told by — the account's own words first
 *  when the account publishes, the bot's otherwise. For `t()`. */
export function problemKeys(
  publisher: "account" | "bot",
  problem: string | null,
  hint = false,
): string[] {
  const base = hint ? "aggregator.problem_hint" : "aggregator.problem";
  const keys = [`${base}.${problem}`];
  return publisher === "account" ? [`${base}_account.${problem}`, ...keys] : keys;
}

const ROOT = "/api/aggregator";
export const CHANNELS_KEY = ["aggregator", "channels"] as const;
export const BOTS_KEY = ["aggregator", "bots"] as const;
const OWN_CHANNELS_KEY = ["aggregator", "own-channels"] as const;
export const PROVIDERS_KEY = ["aggregator", "providers"] as const;
const TRANSLATOR_KEY = ["aggregator", "translator"] as const;
const channelKey = (id: string | null) => ["aggregator", "channel", id] as const;
const postsKey = (id: string | null) => ["aggregator", "posts", id] as const;
const statsKey = (id: string | null) => ["aggregator", "stats", id] as const;

/** The worker reads and publishes on its own; the pages follow it. */
const LIVE_REFRESH_MS = 30_000;

/* ---------------------------------------------------------- once, not twice */

const inFlight = new Map<string, Promise<unknown>>();

/**
 * The same request, sent while its twin is still on the way, is not sent
 * again: both callers get the one answer. A button greyed out while busy does
 * not stop a double click — the second click lands before the redraw.
 */
function once<T>(key: string, send: () => Promise<T>): Promise<T> {
  const running = inFlight.get(key) as Promise<T> | undefined;
  if (running) return running;
  const started = send().finally(() => inFlight.delete(key));
  inFlight.set(key, started);
  return started;
}

/* -------------------------------------------------------------------- bots */

export function useBots() {
  return useQuery<AggBot[]>({ queryKey: BOTS_KEY, queryFn: () => api.get(`${ROOT}/bots`) });
}

/**
 * Have an account make a bot at @BotFather. Half a minute of a conversation
 * in Telegram: the request may outlive the connection that asked, so the list
 * is re-read whichever way it ends — the bot may be there.
 */
export function useCreateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account_id: string) =>
      once(`bot:${account_id}`, () => api.post<AggBot>(`${ROOT}/bots`, { account_id })),
    // Not returned: the caller must hear of a refusal at once, not after the
    // list has been read again.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: BOTS_KEY });
    },
  });
}

export function useDisconnectBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => once(`bot-off:${id}`, () => api.delete<void>(`${ROOT}/bots/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregator"] }),
  });
}

/* --------------------------------------------------------------- providers */

export function useAiProviders() {
  return useQuery<AiProvider[]>({
    queryKey: PROVIDERS_KEY,
    queryFn: () => api.get(`${ROOT}/ai-providers`),
  });
}

export interface ProviderInput {
  name: string;
  base_url: string;
  model: string;
  api_key: string;
  requests_per_minute: number;
}

export function useAddProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProviderInput) =>
      once(`provider:${payload.base_url}:${payload.model}`, () =>
        api.post<AiProvider>(`${ROOT}/ai-providers`, payload),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregator"] }),
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<ProviderInput & { is_active: boolean }>) =>
      api.patch<AiProvider>(`${ROOT}/ai-providers/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregator"] }),
  });
}

export function useRemoveProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      once(`provider-off:${id}`, () => api.delete<void>(`${ROOT}/ai-providers/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregator"] }),
  });
}

export function useTestProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; detail: string }>(`${ROOT}/ai-providers/${id}/test`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

/** Ready addresses for the services most companies use. */
export const PROVIDER_PRESETS = [
  { id: "groq", name: "Groq", base_url: "https://api.groq.com/openai/v1", model: "llama-3.3-70b-versatile" },
  { id: "openai", name: "OpenAI", base_url: "https://api.openai.com/v1", model: "gpt-4o-mini" },
  {
    id: "openrouter",
    name: "OpenRouter",
    base_url: "https://openrouter.ai/api/v1",
    model: "meta-llama/llama-3.3-70b-instruct",
  },
  { id: "deepseek", name: "DeepSeek", base_url: "https://api.deepseek.com/v1", model: "deepseek-chat" },
] as const;

/* -------------------------------------------------------------- translator */

/** What DeepL translates into (the server says the same). */
export const TRANSLATE_LANGUAGES = [
  "ru",
  "en",
  "uk",
  "de",
  "fr",
  "es",
  "it",
  "pt",
  "tr",
  "pl",
  "ar",
  "id",
] as const;

export interface Translator {
  kind: "deepl";
  /** The key's end; a free plan's ends with ":fx". */
  key_hint: string;
  free: boolean;
  last_error: string | null;
  last_ok_at: string | null;
  /** This month: characters translated, and what the plan allows. */
  used: number | null;
  limit: number | null;
}

export function useTranslator() {
  return useQuery<Translator | null>({
    queryKey: TRANSLATOR_KEY,
    queryFn: () => api.get(`${ROOT}/translator`),
  });
}

/** The translator changed: so did every channel's "is there one". */
function useTranslatorChange<TVars>(run: (vars: TVars) => Promise<Translator | void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (out) => {
      if (out) qc.setQueryData(TRANSLATOR_KEY, out);
      void qc.invalidateQueries({ queryKey: ["aggregator"] });
    },
  });
}

export function useSaveTranslator() {
  return useTranslatorChange((api_key: string) =>
    once("translator", () => api.put<Translator>(`${ROOT}/translator`, { api_key })),
  );
}

export function useCheckTranslator() {
  return useTranslatorChange(() =>
    once("translator-check", () => api.post<Translator>(`${ROOT}/translator/check`)),
  );
}

export function useRemoveTranslator() {
  return useTranslatorChange(() =>
    once("translator-off", () => api.delete<void>(`${ROOT}/translator`)),
  );
}

/* ---------------------------------------------------------------- channels */

export function useChannels() {
  return useQuery<ChannelSummary[]>({
    queryKey: CHANNELS_KEY,
    queryFn: () => api.get(`${ROOT}/channels`),
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === "active") ? LIVE_REFRESH_MS : false,
  });
}

export function useChannel(id: string | null) {
  return useQuery<ChannelDetail>({
    queryKey: channelKey(id),
    enabled: id !== null,
    queryFn: () => api.get(`${ROOT}/channels/${id}`),
    refetchInterval: (query) => (query.state.data?.status === "active" ? LIVE_REFRESH_MS : false),
  });
}

/** A change to one channel: its card comes back in the answer; the list is re-read. */
function useChannelChange<TVars extends { id: string }>(
  run: (vars: TVars) => Promise<ChannelDetail>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async (out, vars) => {
      // A read of the card sent before this change must not land on top of it.
      await qc.cancelQueries({ queryKey: channelKey(vars.id) });
      qc.setQueryData(channelKey(vars.id), out);
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: postsKey(vars.id) });
    },
  });
}

/** The company's own channels to choose from; read when the dialog opens. */
export function useOwnChannels(enabled: boolean) {
  return useQuery<OwnChannel[]>({
    queryKey: OWN_CHANNELS_KEY,
    queryFn: () => api.get(`${ROOT}/own-channels`),
    enabled,
    staleTime: 0,
  });
}

/** Add one of the company's own channels: the account that runs it makes the
 *  bot its admin. Without `bot_id` — the company's bot that works. */
export function useAddChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { own_chat_id: string; bot_id?: string }) =>
      once(`channel:${payload.own_chat_id}`, () =>
        api.post<ChannelDetail>(`${ROOT}/channels`, payload),
      ),
    onSuccess: (created) => {
      qc.setQueryData(channelKey(created.id), created);
      qc.invalidateQueries({ queryKey: BOTS_KEY });
      qc.invalidateQueries({ queryKey: OWN_CHANNELS_KEY });
    },
    // Whichever way it ended: with no answer at all the channel may have been
    // added, and a list that is read only on success would not show it.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
    },
  });
}

export function useUpdateChannel() {
  return useChannelChange(
    ({ id, ...patch }: { id: string; status?: AggStatus; settings?: ChannelSettings }) =>
      api.patch<ChannelDetail>(`${ROOT}/channels/${id}`, patch),
  );
}

/** Give the channel its bot — again, or another one: the account that runs
 *  the channel makes the bot its admin. Without `bot_id` — the bot it has, or
 *  the company's one that works. */
export function useGiveBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bot_id }: { id: string; bot_id?: string }) =>
      once(`give-bot:${id}:${bot_id ?? ""}`, () =>
        api.post<ChannelDetail>(`${ROOT}/channels/${id}/bot`, { bot_id: bot_id ?? null }),
      ),
    onSuccess: async (out, vars) => {
      await qc.cancelQueries({ queryKey: channelKey(vars.id) });
      qc.setQueryData(channelKey(vars.id), out);
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
    },
    // Tried on a bot whose token is dead, the bot is marked so — and has to
    // be listed so.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: BOTS_KEY });
    },
  });
}

/** Switch every channel of the company off at once. */
export function usePauseAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => once("pause-all", () => api.post<{ paused: number }>(`${ROOT}/pause-all`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregator"] }),
  });
}

export function useCheckChannel() {
  return useChannelChange(({ id }: { id: string }) =>
    api.post<ChannelDetail>(`${ROOT}/channels/${id}/check`),
  );
}

export function useCopySettings() {
  return useChannelChange(({ id, from_channel_id }: { id: string; from_channel_id: string }) =>
    api.post<ChannelDetail>(`${ROOT}/channels/${id}/copy-settings`, { from_channel_id }),
  );
}

export function useRemoveChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      once(`channel-off:${id}`, () => api.delete<void>(`${ROOT}/channels/${id}`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: BOTS_KEY });
    },
  });
}

/* ----------------------------------------------------------------- sources */

/** Read a channel's page and show what would be taken — before it is added. */
export function usePreviewSource() {
  return useMutation({
    mutationFn: (ref: string) => api.post<SourcePreview>(`${ROOT}/sources/preview`, { ref }),
  });
}

function useAfterChannelChange() {
  const qc = useQueryClient();
  return (id: string) => {
    qc.invalidateQueries({ queryKey: channelKey(id) });
    qc.invalidateQueries({ queryKey: CHANNELS_KEY });
    qc.invalidateQueries({ queryKey: postsKey(id) });
    qc.invalidateQueries({ queryKey: statsKey(id) });
  };
}

export function useAddSources() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      once(`sources:${id}:${text}`, () =>
        api.post<SourcesAdded>(`${ROOT}/channels/${id}/sources`, { text }),
      ),
    onSuccess: (_out, vars) => refresh(vars.id),
  });
}

/* ------------------------------------------------------------------- fills */

/** A fill started or stopped: the card comes back in the answer. Refused —
 *  started from another window a moment ago, or ended meanwhile — the card
 *  is read again, so that it shows what the refusal was about. */
function useFillChange<TVars extends { id: string }>(
  run: (vars: TVars) => Promise<ChannelDetail>,
) {
  const qc = useQueryClient();
  const change = useChannelChange(run);
  return {
    ...change,
    mutateAsync: async (vars: TVars) => {
      try {
        return await change.mutateAsync(vars);
      } catch (err) {
        void qc.invalidateQueries({ queryKey: channelKey(vars.id) });
        void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
        throw err;
      }
    },
  };
}

/** Fill the channel with its sources' older posts. */
export function useStartFill() {
  return useFillChange(({ id, ...ask }: { id: string } & FillInput) =>
    once(`fill:${id}`, () => api.post<ChannelDetail>(`${ROOT}/channels/${id}/fills`, ask)),
  );
}

/** Stop a fill: what has not gone out is let go. */
export function useStopFill() {
  return useFillChange(({ fill_id }: { id: string; fill_id: string }) =>
    once(`fill-stop:${fill_id}`, () => api.post<ChannelDetail>(`${ROOT}/fills/${fill_id}/stop`)),
  );
}

export function useRemoveSource() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({ id, source_id }: { id: string; source_id: string }) =>
      once(`source-off:${id}:${source_id}`, () =>
        api.delete<void>(`${ROOT}/channels/${id}/sources/${source_id}`),
      ),
    onSuccess: (_out, vars) => refresh(vars.id),
  });
}

export function useSetSourceStatus() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({ source_id, status }: { id: string; source_id: string; status: AggStatus }) =>
      api.patch<void>(`${ROOT}/sources/${source_id}`, { status }),
    onSuccess: (_out, vars) => refresh(vars.id),
  });
}

/** Hand a source whose public page shows nothing to an account of the fleet:
 *  it joins by the source's name and reads. */
export function useSourceByAccount() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({ source_id }: { id: string; source_id: string }) =>
      once(`source-by-account:${source_id}`, () =>
        api.post<void>(`${ROOT}/sources/${source_id}/by-account`),
      ),
    onSuccess: (_out, vars) => refresh(vars.id),
  });
}

/** The settings tried on the sources' real posts; nothing is published. */
export function useDryRun() {
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      settings?: ChannelSettings;
      source_id?: string;
      use_ai?: boolean;
    }) => api.post<DryRun>(`${ROOT}/channels/${id}/dry-run`, body),
  });
}

/* ------------------------------------------------------------------- posts */

export function useDeliveries(
  id: string | null,
  view: DeliveryView,
  filter: { only?: DeliveryStatus; source_id?: string; limit?: number } = {},
  live = true,
) {
  return useQuery<DeliveriesPage>({
    queryKey: [...postsKey(id), view, filter],
    enabled: id !== null,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
    // "Show more" asks for a longer list: until it comes, the one on screen stays.
    placeholderData: (shown) => shown,
    queryFn: () => {
      const params = new URLSearchParams({ view, limit: String(filter.limit ?? 30) });
      if (filter.only) params.set("only", filter.only);
      if (filter.source_id) params.set("source_id", filter.source_id);
      return api.get<DeliveriesPage>(`${ROOT}/channels/${id}/deliveries?${params}`);
    },
  });
}

export type DeliveryAction =
  | "approve"
  | "reject"
  | "retry"
  | "publish-now"
  | "unpublish";

/** A change to one post: the lists it may sit in are re-read. */
function useDeliveryChange<TVars extends { delivery: Delivery }, TOut>(
  run: (vars: TVars) => Promise<TOut>,
) {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: run,
    // Also after a refusal: the post may have moved on while the page stood still.
    onSettled: (_out, _err, vars) => refresh(vars.delivery.channel_id),
  });
}

export function useDeliveryAction() {
  return useDeliveryChange(({ delivery, action }: { delivery: Delivery; action: DeliveryAction }) =>
    once(`${action}:${delivery.id}`, () =>
      api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/${action}`),
    ),
  );
}

export function useEditDelivery() {
  return useDeliveryChange(({ delivery, text }: { delivery: Delivery; text: string }) =>
    delivery.status === "published"
      ? api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/fix-text`, { text })
      : api.patch<Delivery>(`${ROOT}/deliveries/${delivery.id}`, { text }),
  );
}

export function useRegenerateDelivery() {
  return useDeliveryChange(({ delivery, hint }: { delivery: Delivery; hint: string }) =>
    once(`regenerate:${delivery.id}`, () =>
      api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/regenerate`, { hint }),
    ),
  );
}

/** Take one picture or video out of a post — before it goes, or out of the channel. */
export function useDropMedia() {
  return useDeliveryChange(
    ({ delivery, index, url }: { delivery: Delivery; index: number; url: string }) =>
      once(`drop:${delivery.id}:${url}`, () =>
        api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/drop-media`, { index, url }),
      ),
  );
}

export function usePinDelivery() {
  return useDeliveryChange(({ delivery, pinned }: { delivery: Delivery; pinned: boolean }) =>
    once(`pin:${delivery.id}:${pinned}`, () =>
      api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/pin`, { pinned }),
    ),
  );
}

export type WriteWhen = "now" | "next" | "at";

/** A file of a post of one's own, as the page read it: a video's frame,
 *  length and a still for its cover. */
export interface WrittenFile {
  file: File;
  kind: "photo" | "video";
  width?: number;
  height?: number;
  duration?: number;
  cover?: Blob;
}

/** A post of one's own: out at once, next in the queue, or at a set moment. */
export function useWritePost() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({
      channelId,
      text,
      when,
      at,
      files,
    }: {
      channelId: string;
      text: string;
      when: WriteWhen;
      at: string | null;
      files: WrittenFile[];
    }) => {
      const form = new FormData();
      form.set("text", text);
      form.set("when", when);
      if (when === "at" && at) form.set("at", at);
      const covers: Blob[] = [];
      const meta = files.map((item) => {
        if (item.kind !== "video") return {};
        const thumb = item.cover ? covers.push(item.cover) - 1 : null;
        return { width: item.width, height: item.height, duration: item.duration, thumb };
      });
      form.set("meta", JSON.stringify(meta));
      for (const item of files) form.append("files", item.file, item.file.name);
      covers.forEach((cover, n) => form.append("thumbs", cover, `cover${n}.jpg`));
      const key = [channelId, when, at, text, ...files.map((f) => `${f.file.name}:${f.file.size}`)];
      return once(`write:${key.join("|")}`, () =>
        api.upload<Delivery>(`${ROOT}/channels/${channelId}/posts`, form),
      );
    },
    onSettled: (_out, _err, vars) => refresh(vars.channelId),
  });
}

export function useMoveDelivery() {
  return useDeliveryChange(({ delivery, to }: { delivery: Delivery; to: "top" | "up" | "down" }) =>
    api.post<Delivery>(`${ROOT}/deliveries/${delivery.id}/move`, { to }),
  );
}

export function useBulkDeliveries() {
  const refresh = useAfterChannelChange();
  return useMutation({
    mutationFn: ({
      id,
      ids,
      action,
    }: {
      id: string;
      ids: string[];
      action: "approve" | "reject" | "retry";
    }) =>
      once(`bulk:${action}:${id}`, () =>
        api.post<{ done: number; skipped: number }>(`${ROOT}/channels/${id}/deliveries/bulk`, {
          ids,
          action,
        }),
      ),
    onSettled: (_out, _err, vars) => refresh(vars.id),
  });
}

/** `live` — the channel is working: its numbers move, and so are re-read. */
export function useChannelStats(id: string | null, live: boolean, days = 7) {
  return useQuery<ChannelStats>({
    queryKey: [...statsKey(id), days],
    enabled: id !== null,
    queryFn: () => api.get(`${ROOT}/channels/${id}/stats?days=${days}`),
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  });
}

/* ------------------------------------------------------- what the page says */

/** How a channel is doing, as one word for its badge. */
export function channelState(channel: ChannelSummary): "working" | "paused" | "stopped" | "idle" {
  if (channel.status === "paused") return "paused";
  if (channel.problem !== null) return "stopped";
  return channel.sources === 0 ? "idle" : "working";
}

export function channelTone(channel: ChannelSummary): Tone {
  return { working: "success", paused: "warning", stopped: "danger", idle: "info" }[
    channelState(channel)
  ] as Tone;
}

export function deliveryTone(status: DeliveryStatus): Tone {
  switch (status) {
    case "published":
      return "success";
    case "queued":
    case "publishing":
    case "pending":
      return "info";
    case "review":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
}

/** The settings a channel starts with — the server's defaults, for a blank form. */
export const FILTER_KINDS: readonly FilterKind[] = [
  "stop_words",
  "must_words",
  "looks_like_ad",
  "marked_ad",
  "forward",
  "forward_from",
  "reply",
  "link",
  "channel_link",
  "language",
  "min_length",
  "max_length",
  "no_media",
  "has_media",
  "missing_media",
];

/** Rules most channels want, offered ready-made: one click adds each. */
export const READY_RULES: readonly FilterKind[] = [
  "looks_like_ad",
  "no_media",
  "marked_ad",
  "forward",
];

/** Kinds that need something typed in to mean anything. */
export const KINDS_WITH_VALUE: ReadonlySet<FilterKind> = new Set([
  "stop_words",
  "must_words",
  "forward_from",
  "language",
  "min_length",
  "max_length",
]);

/**
 * What is wrong with a rule as it stands in the form, or null. The server
 * refuses the same — but only when everything is saved, and for the whole
 * form at once; the page says it by the rule.
 */
export function ruleProblem(rule: FilterRule): "needs_value" | "needs_number" | null {
  if (!KINDS_WITH_VALUE.has(rule.kind)) return null;
  const value = rule.value.trim();
  if (!value) return "needs_value";
  if (rule.kind.endsWith("_length") && !/^\d+$/.test(value)) return "needs_number";
  return null;
}

/** Whether the channel publishes in this hour of the day (the server's `Schedule.open_at`). */
export function openAt(schedule: ChannelSettings["schedule"], hour: number): boolean {
  const { hour_from: start, hour_to: end } = schedule;
  if (start === end % 24) return true;
  if (start < end) return start <= hour && hour < end;
  return hour >= start || hour < end;
}

/** The exact times that fall outside the channel's hours: no post would go out at them. */
export function closedSlots(schedule: ChannelSettings["schedule"]): string[] {
  return schedule.slots.filter((slot) => !openAt(schedule, Number(slot.slice(0, 2))));
}

/**
 * What in the settings cannot be saved as it is — and so where to take the
 * person: an unfinished rule, a replacement with nothing to look for, an
 * exact time outside the channel's hours.
 */
export function settingsProblem(
  settings: ChannelSettings,
): "rule" | "replacement" | "slot" | "views" | null {
  if (settings.filters.some((rule) => ruleProblem(rule) !== null)) return "rule";
  if (settings.cleanups.replacements.some((item) => !item.find.trim())) return "replacement";
  if (closedSlots(settings.schedule).length > 0) return "slot";
  // A post is looked at the minute it is out, with no views yet: without a
  // delay the threshold would let nothing through.
  if (settings.min_views > 0 && settings.schedule.delay_minutes === 0) return "views";
  return null;
}

/** A post's picture as the page may show it: through this server — the
 *  panel's security headers let it load pictures from nowhere else. */
/**
 * The order accounts are offered in for what is done once and has to last —
 * making the company's bot, owning a channel: one @SpamBot limited goes last
 * (Telegram does not let it create a channel at all), one that finished its
 * warmup before one that did not, then the healthier, then the one that has
 * been with us longer.
 */
export function byReliability(a: AccountSummary, b: AccountSummary): number {
  const rank = (x: AccountSummary) =>
    (x.spam_state === "limited" || x.is_peer_flood_permanent ? 8 : 0) +
    (x.warmup_completed ? 0 : 4) +
    (x.peer_flood_until && Date.parse(x.peer_flood_until) > Date.now() ? 1 : 0);
  return (
    rank(a) - rank(b) ||
    (b.health_score ?? 0) - (a.health_score ?? 0) ||
    Date.parse(a.created_at) - Date.parse(b.created_at) ||
    a.seq_no - b.seq_no
  );
}

export function pictureSrc(url: string): string {
  return `${ROOT}/media?url=${encodeURIComponent(url)}`;
}

export function newRuleId(): string {
  return Math.random().toString(16).slice(2, 12).padEnd(10, "0");
}

/** Whether two settings documents are the same — to know there is something to save. */
export function sameSettings(a: ChannelSettings, b: ChannelSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * How many of the queued posts will be let go before their turn: with this
 * interval a post further down the queue waits longer than the channel keeps
 * one. Zero when the queue clears in time.
 */
export function willExpire(queued: number, schedule: ChannelSettings["schedule"]): number {
  if (schedule.slots.length > 0 || schedule.interval_minutes <= 0) return 0;
  const fits = Math.floor((schedule.stale_hours * 60) / (schedule.interval_minutes * 1.15)) + 1;
  return Math.max(0, queued - fits);
}

/** An invite link: a closed channel or group, which an account joins and reads. */
const INVITE_LINK = /(?:t|telegram)\.me\/(?:\+|joinchat\/)([A-Za-z0-9_-]{8,60})/i;

/** The hash of the invite link in a line. A link of digits alone is a person's
 *  phone number ("t.me/+7916…"), not an invitation — the server says the same. */
function inviteHash(line: string): string | null {
  const found = INVITE_LINK.exec(line);
  return found && !/^\d+$/.test(found[1]) ? found[1] : null;
}

export function isInviteLink(line: string): boolean {
  return inviteHash(line) !== null;
}

/** What a source is called where there is room for one name: its title, else
 *  its @name. A closed one nobody has got into yet has neither — `closed`
 *  says so in words. */
export function sourceLabel(
  source: { title: string | null; username: string },
  closed: string,
): string {
  if (source.title) return source.title;
  return source.username.startsWith("+") ? closed : `@${source.username}`;
}

/** The sources a pasted text names — "@name" lines and invite links — for
 *  the count under the field. */
export function countSourceLines(text: string): number {
  const seen = new Set<string>();
  const lines = text.split("\n").filter((line) => line.trim());
  for (const line of lines) {
    const invite = inviteHash(line);
    if (invite) {
      seen.add(`+${invite}`);
      continue;
    }
    const link = /(?:t|telegram)\.me\/(?:s\/)?@?([A-Za-z][A-Za-z0-9_]{3,31})/i.exec(line);
    const handle = link ?? /@([A-Za-z][A-Za-z0-9_]{3,31})\b/.exec(line);
    const bare = lines.length === 1 ? /^\s*([A-Za-z][A-Za-z0-9_]{3,31})\s*$/.exec(line) : null;
    const found = handle ?? bare;
    if (found) seen.add(found[1].toLowerCase());
  }
  return seen.size;
}
